import { Button } from "@/components/ui/baseUi/button"
import { FieldLabel } from "@/components/ui/baseUi/field"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { boxProgramColors, type BoxProgramColorType } from "@/modules/boxProgrammation/components/boxProgramColors"
import { ColorCircle } from "@/modules/boxProgrammation/components/boxProgramColorSelection"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
  getSlotDurationOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import { CoachCategoryFormStepper } from "@modules/coachCategories/components/coachCategoryFormStepper"
import {
  getCoachCategoryDefaultSettingsSchema,
  type CoachCategoryDefaultSettingsFormFields,
} from "@modules/coachCategories/formTypes/coachCategoryFormTypes"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import { LoaderCircle } from "lucide-react"

export const CoachCategoryDefaultSettingsForm = withForm({
  defaultValues: {} as CoachCategoryDefaultSettingsFormFields,
  props: {} as {
    categoryName: string
    categoryColor: BoxProgramColorType
    moveToPreviousFormStep: () => void
    onValidate: () => void
    isSubmitting: boolean
  } & React.ComponentProps<"div">,
  render: function CoachCategoryDefaultSettingsFormInner({
    form,
    categoryName,
    categoryColor,
    onValidate,
    isSubmitting,
    moveToPreviousFormStep,
  }) {
    const schema = getCoachCategoryDefaultSettingsSchema()
    const durationOptions = getSlotDurationOptions()
    const registrationBeginningOptions = getRegistrationBeginningOptions()
    const registrationEndOptions = getRegistrationEndOptions()
    const cancellationBeginningOptions = getCancellationBeginningOptions()

    return (
      <div className="flex flex-col gap-6 px-5 pt-18 pb-6">
        <CoachCategoryFormStepper step={2} />

        <div className="flex items-center gap-2">
          <Text fontStyle="TL">{categoryName}</Text>
          <ColorCircle $color={boxProgramColors[categoryColor].background} $striped />
        </div>

        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault()
            onValidate()
          }}
        >
          <div className="flex flex-col gap-2">
            <Text fontStyle="TM">{word("box.activityDefaultValues.form.title")}</Text>
            <HUCardAlert description={word("box.activityDefaultValues.form.explanation")} />
          </div>

          <form.AppField
            name="duration"
            validators={{ onChange: schema.shape.duration, onBlur: schema.shape.duration }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.slotTemplateGroup.form.slotDuration.label")}</field.Label>
                <field.Content>
                  <HUTimeDropdownOrCustomizedTimeInput
                    name="duration"
                    dropdownOptions={durationOptions}
                    getFieldProps={() => ({
                      name: "duration",
                      value: field.state.value,
                      onChange: () => {
                        return
                      },
                      onBlur: field.handleBlur,
                    })}
                    setFieldValue={(_name, value) => {
                      field.handleChange(value as number)
                      return Promise.resolve()
                    }}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField
            name="participantLimit"
            validators={{
              onChange: schema.shape.participantLimit,
              onBlur: schema.shape.participantLimit,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.slotTemplateGroup.form.participantLimit.label")}</field.Label>
                <field.Content>
                  <div className="flex flex-row items-center gap-2">
                    <field.NumberInput
                      step={1}
                      max={BOX_SLOT_PARTICIPANTS_LIMIT_MAX}
                      min={BOX_SLOT_PARTICIPANTS_LIMIT_MIN}
                      placeholder={word("box.slotTemplateGroup.form.participantLimit.placeholder")}
                    />
                    <Text fontStyle="BM">
                      {word("coach.planning.category.resume.participantLimit.optional_plural")}
                    </Text>
                  </div>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.Subscribe selector={(s) => s.values.trialsAccepted}>
            {(trialsAccepted) => (
              <div
                className={cn(
                  "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-2 border-interactive-border-neutral-default bg-surface-background-secondary px-4 py-3 transition-all duration-100 hover:border-interactive-border-primary-default",
                )}
                onClick={() => {
                  form.setFieldValue("trialsAccepted", !trialsAccepted)
                }}
              >
                {trialsAccepted ? (
                  <TrialSessionSelectedIcon textColor="var(--textOnSurface-primary-subtle)" color="" />
                ) : (
                  <TrialSessionIcon color="" />
                )}
                <Text
                  fontStyle="TS"
                  className={cn("text-center", trialsAccepted ? "text-textDefault-body" : "text-textDisabled")}
                >
                  {trialsAccepted
                    ? word("box.slotTemplateGroup.form.trialSession.allowed")
                    : word("box.slotTemplateGroup.form.trialSession.rejected")}
                </Text>
              </div>
            )}
          </form.Subscribe>

          <div className="flex flex-col gap-2">
            <FieldLabel required>{word("box.slotTemplateGroup.form.registration.label")}</FieldLabel>
            <Text fontStyle="BS" className="font-semibold text-textOnSurface-muted">
              {word("box.slotTemplateGroup.form.registration.description")}
            </Text>
            <div className="flex gap-2">
              <form.AppField
                name="limitStart"
                validators={{
                  onChange: schema.shape.limitStart,
                  onBlur: schema.shape.limitStart,
                }}
              >
                {(field) => (
                  <field.Field>
                    <field.Label>{word("box.slotTemplateGroup.form.registration.beginning")}</field.Label>
                    <field.Content>
                      <HUTimeDropdownOrCustomizedTimeInput
                        name="limitStart"
                        dropdownOptions={registrationBeginningOptions}
                        getFieldProps={() => ({
                          name: "limitStart",
                          value: field.state.value,
                          onChange: () => {
                            return
                          },
                          onBlur: field.handleBlur,
                        })}
                        setFieldValue={(_name, value) => {
                          field.handleChange(value as number)
                          return Promise.resolve()
                        }}
                      />
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>
              <form.AppField
                name="limitEnd"
                validators={{
                  onChange: schema.shape.limitEnd,
                  onBlur: schema.shape.limitEnd,
                }}
              >
                {(field) => (
                  <field.Field>
                    <field.Label>{word("box.slotTemplateGroup.form.registration.end")}</field.Label>
                    <field.Content>
                      <HUTimeDropdownOrCustomizedTimeInput
                        name="limitEnd"
                        dropdownOptions={registrationEndOptions}
                        getFieldProps={() => ({
                          name: "limitEnd",
                          value: field.state.value,
                          onChange: () => {
                            return
                          },
                          onBlur: field.handleBlur,
                        })}
                        setFieldValue={(_name, value) => {
                          field.handleChange(value as number)
                          return Promise.resolve()
                        }}
                      />
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>
            </div>
          </div>

          <form.AppField
            name="limitCancel"
            validators={{
              onChange: schema.shape.limitCancel,
              onBlur: schema.shape.limitCancel,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.slotTemplateGroup.form.cancellation.label")}</field.Label>
                <field.Content>
                  <HUTimeDropdownOrCustomizedTimeInput
                    name="limitCancel"
                    dropdownOptions={cancellationBeginningOptions}
                    getFieldProps={() => ({
                      name: "limitCancel",
                      value: field.state.value,
                      onChange: () => {
                        return
                      },
                      onBlur: field.handleBlur,
                    })}
                    setFieldValue={(_name, value) => {
                      field.handleChange(value as number)
                      return Promise.resolve()
                    }}
                  />
                  <field.Description className="font-semibold">
                    {word("box.slotTemplateGroup.form.cancellation.description")}
                  </field.Description>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField
            name="location"
            validators={{
              onChange: schema.shape.location,
              onBlur: schema.shape.location,
            }}
          >
            {(field) => (
              <field.Field>
                <field.Label>{word("box.slotTemplateGroup.form.area.title")}</field.Label>
                <field.Content>
                  <field.Input placeholder={word("coach.planning.category.form.area.placeholder")} />
                  <field.Description className="font-semibold">
                    {word("box.slotTemplateGroup.form.area.subtitle")}
                  </field.Description>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <div className="mt-4 mb-12 flex justify-end">
            <div className="flex w-full grow flex-col gap-4 sm:flex-row">
              <Button variant="outline" size="lg" onClick={moveToPreviousFormStep} className="grow">
                {word("global.previous")}
              </Button>
              <Button
                isLoading={isSubmitting}
                variant="primary"
                size="lg"
                className="grow"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting && <LoaderCircle className="animate-spin" />}
                {word("global.next")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    )
  },
})
